import React, { useCallback, useEffect, useState } from "react";
import { MessageSquareHeart, MessageCircleQuestion, Star, Check, X, Mic, Square, Send, Trash2 } from "lucide-react";
import { useVoiceRecorder } from "@/components/AskTutor";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;

const FeedbackCard = ({ f, onDone }) => {
  const [quote, setQuote] = useState(f.comment);
  const [name, setName] = useState(f.name);
  const [role, setRole] = useState(`${f.course} student`);
  const publish = async () => {
    try { await api.post(`/admin/feedback/${f.id}/publish`, { quote, name, role }); toast.success("Published to the testimonials"); onDone(); }
    catch { toast.error("Could not publish"); }
  };
  const dismiss = async () => { await api.post(`/admin/feedback/${f.id}/dismiss`); onDone(); };
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`feedback-item-${f.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="flex">{[...Array(f.rating)].map((_, i) => <Star key={i} size={13} className="fill-primary text-primary" />)}</span>
        <span>{f.course}</span><span>·</span><span>{f.email}</span>
        {!f.allow_testimonial && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-red-500">Private, not for publishing</span>}
        <span className={`ml-auto rounded-full px-2 py-0.5 ${f.status === "pending" ? "bg-primary/15 text-primary" : "bg-secondary"}`}>{f.status}</span>
      </div>
      {f.status === "pending" ? (
        <>
          <textarea value={quote} onChange={(e) => setQuote(e.target.value)} rows={3} maxLength={600} className="mt-3 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`feedback-edit-quote-${f.id}`} />
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} className="border-border bg-background" data-testid={`feedback-edit-name-${f.id}`} />
            <Input value={role} onChange={(e) => setRole(e.target.value)} className="border-border bg-background" data-testid={`feedback-edit-role-${f.id}`} />
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={publish} disabled={!f.allow_testimonial} className="rounded-full bg-primary text-primary-foreground" data-testid={`feedback-publish-${f.id}`}><Check size={14} className="mr-1" /> Publish as testimonial</Button>
            <Button size="sm" variant="outline" onClick={dismiss} className="rounded-full border-border" data-testid={`feedback-dismiss-${f.id}`}><X size={14} className="mr-1" /> Dismiss</Button>
          </div>
        </>
      ) : <p className="mt-3 text-sm text-muted-foreground">{f.comment}</p>}
    </div>
  );
};

export const FeedbackReview = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/feedback").then(({ data }) => setItems(data.feedback || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="mt-14" data-testid="admin-feedback">
      <div className="flex items-center gap-2"><MessageSquareHeart size={18} className="text-primary" /><h2 className="font-serif text-2xl">Course feedback</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Student feedback lands here as pending. Edit the wording, then publish it to the website testimonials.</p>
      <div className="mt-5 space-y-3">
        {items.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="feedback-empty">No feedback yet.</p> : items.map((f) => <FeedbackCard key={f.id} f={f} onDone={load} />)}
      </div>
    </section>
  );
};

const ReplyBox = ({ q, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const voice = useVoiceRecorder();
  const send = async () => {
    if (!text.trim() && !voice.blob) return toast.error("Type a reply or record a voice note");
    const fd = new FormData();
    fd.append("message", text.trim());
    if (voice.blob) fd.append("file", new File([voice.blob], "reply.webm", { type: voice.blob.type }));
    setBusy(true);
    try { await api.post(`/admin/tutor-questions/${q.id}/reply`, fd); toast.success("Reply emailed to the student"); setText(""); voice.clear(); onDone(); }
    catch { toast.error("Could not send reply"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-xl border border-border bg-background p-3" data-testid={`reply-box-${q.id}`}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={4000} placeholder="Type your reply…" className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm" data-testid={`reply-text-${q.id}`} />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!voice.recording ? (
          <Button size="sm" variant="outline" onClick={voice.start} className="rounded-full border-border" data-testid={`reply-record-${q.id}`}><Mic size={14} className="mr-1" /> Voice reply</Button>
        ) : (
          <Button size="sm" onClick={voice.stop} className="rounded-full bg-red-600 text-white" data-testid={`reply-stop-${q.id}`}><Square size={12} className="mr-1" /> Stop · {voice.secs}s</Button>
        )}
        {voice.blob && <><audio controls src={URL.createObjectURL(voice.blob)} className="h-8" /><button onClick={voice.clear} aria-label="Remove voice note"><Trash2 size={14} /></button></>}
        <Button size="sm" onClick={send} disabled={busy || voice.recording} className="ml-auto rounded-full bg-primary text-primary-foreground" data-testid={`reply-send-${q.id}`}><Send size={14} className="mr-1" /> {busy ? "Sending…" : "Send reply"}</Button>
      </div>
    </div>
  );
};

export const TutorInbox = () => {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const isTake = (q) => (q.message || "").startsWith("Play-along take");
  const shown = tab === "takes" ? items.filter(isTake) : items;
  const load = useCallback(() => api.get("/admin/tutor-questions").then(({ data }) => setItems(data.questions || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return (
    <section className="mt-14" data-testid="admin-tutor-questions">
      <div className="flex items-center gap-2"><MessageCircleQuestion size={18} className="text-primary" /><h2 className="font-serif text-2xl">Ask your tutor</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Questions from students. Each one is also emailed to you. Reply within 24 hours.</p>
      <div className="mt-4 flex gap-2">
        {[["all", `All questions (${items.length})`], ["takes", `Recorded takes (${items.filter(isTake).length})`]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`tutor-tab-${v}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${tab === v ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:border-primary"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-5 space-y-3">
        {shown.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="tutor-questions-empty">{tab === "takes" ? "No recorded takes yet." : "No questions yet."}</p> : shown.map((q) => (
          <div key={q.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`tutor-question-${q.id}`}>
            <p className="text-xs text-muted-foreground">{q.name} · <a href={`mailto:${q.email}`} className="text-primary">{q.email}</a> · {q.course} · {new Date(q.created_at).toLocaleString()}</p>
            {isTake(q) && <span className="mt-2 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary" data-testid={`take-badge-${q.id}`}>Recorded take</span>}
            {q.message && <p className="mt-2 whitespace-pre-line text-sm">{q.message}</p>}
            {q.attachment_kind === "audio" && <audio controls src={`${BASE}${q.attachment_url}`} className="mt-3 w-full" />}
            {q.attachment_kind === "video" && <video controls src={`${BASE}${q.attachment_url}`} className="mt-3 max-h-80 rounded-xl" />}
            {q.reply ? (
              <div className="mt-4 border-l-2 border-primary pl-3 text-sm" data-testid={`tutor-reply-${q.id}`}>
                <p className="text-xs font-medium text-primary">Your reply · {new Date(q.reply.created_at).toLocaleString()}</p>
                {q.reply.message && <p className="mt-1 whitespace-pre-line">{q.reply.message}</p>}
                {q.reply.attachment_kind === "audio" && <audio controls src={`${BASE}${q.reply.attachment_url}`} className="mt-2 w-full" />}
              </div>
            ) : <ReplyBox q={q} onDone={load} />}
          </div>
        ))}
      </div>
    </section>
  );
};
